import type { OnboardingAnswers, Question } from "../../types/onboarding";
import {
	isAdditionalInfoQuestion,
	isFertilityJourneyTypesQuestion,
	isFertilityPathQuestion,
	isFertilityRoleQuestion,
	isFertilityStatusQuestion,
	isGenderRelationshipQuestion,
	isHowDidYouHearQuestion,
	isInterestsQuestion,
	isNeedsMostQuestion,
	isPersonalInfoQuestion,
	isProfileSelectQuestion,
	isSelectProviderQuestion,
	isSetupProfileDetailsQuestion,
	isSetupProfileQuestion,
} from "../../utils/questionTypeGuards";
import { AdditionalInfoQuestion } from "./questions/AdditionalInfoQuestion";
import { FertilityJourneyTypesQuestion } from "./questions/FertilityJourneyTypesQuestion";
import { FertilityPathQuestion } from "./questions/FertilityPathQuestion";
import { FertilityRoleQuestion } from "./questions/FertilityRoleQuestion";
import { FertilityStatusQuestion } from "./questions/FertilityStatusQuestion";
import { GenderRelationshipQuestion } from "./questions/GenderRelationshipQuestion";
import { HowDidYouHearQuestion } from "./questions/HowDidYouHearQuestion";
import { InterestsQuestion } from "./questions/InterestsQuestion";
import { NeedsMostQuestion } from "./questions/NeedsMostQuestion";
import { PersonalInfoQuestion } from "./questions/PersonalInfoQuestion";
import { ProfileSelectQuestion } from "./questions/ProfileSelectQuestion";
import { SelectProviderQuestion } from "./questions/SelectProviderQuestion";
import { SetupProfileDetailsQuestion } from "./questions/SetupProfileDetailsQuestion";
import { SetupProfileQuestion } from "./questions/SetupProfileQuestion";

interface QuestionRendererProps {
	question: Question;
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
}

export function QuestionRenderer({
	question,
	answers,
	onInputChange,
	showValidation = false,
}: QuestionRendererProps) {
	if (isSetupProfileQuestion(question)) {
		return (
			<SetupProfileQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isSetupProfileDetailsQuestion(question)) {
		return (
			<SetupProfileDetailsQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isFertilityStatusQuestion(question)) {
		return (
			<FertilityStatusQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isFertilityRoleQuestion(question)) {
		return (
			<FertilityRoleQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isFertilityJourneyTypesQuestion(question)) {
		return (
			<FertilityJourneyTypesQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isNeedsMostQuestion(question)) {
		return (
			<NeedsMostQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isHowDidYouHearQuestion(question)) {
		return (
			<HowDidYouHearQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isProfileSelectQuestion(question)) {
		return (
			<ProfileSelectQuestion
				question={question}
				currentValue={(() => {
					const value = answers[question.field];
					return typeof value === "string" ? value : "";
				})()}
				onInputChange={onInputChange}
			/>
		);
	}

	if (isPersonalInfoQuestion(question)) {
		return (
			<PersonalInfoQuestion
				answers={answers}
				onInputChange={onInputChange}
				showValidation={showValidation}
			/>
		);
	}

	if (isGenderRelationshipQuestion(question)) {
		return (
			<GenderRelationshipQuestion
				question={question}
				answers={answers}
				onInputChange={onInputChange}
			/>
		);
	}

	if (isFertilityPathQuestion(question)) {
		return (
			<FertilityPathQuestion
				question={question}
				answers={answers}
				onInputChange={onInputChange}
			/>
		);
	}

	if (isInterestsQuestion(question)) {
		return (
			<InterestsQuestion
				question={question}
				answers={answers}
				onInputChange={onInputChange}
			/>
		);
	}

	if (isSelectProviderQuestion(question)) {
		return (
			<SelectProviderQuestion
				question={question}
				answers={answers}
				onInputChange={onInputChange}
			/>
		);
	}

	if (isAdditionalInfoQuestion(question)) {
		return (
			<AdditionalInfoQuestion
				question={question}
				answers={answers}
				onInputChange={onInputChange}
			/>
		);
	}

	console.warn(`Unknown question type: ${question.type}`);
	return null;
}
